import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { storedSession } from '../AppShell';
import { api, btnDanger, btnPrimary, btnSecondary, card, InlineError, input, labelCls, Modal, useToast } from '../ui/Kit';
import { fmtDate, fmtDateTime, isAdmin, PENDING_LABEL, PrivacyHint, REJECT_LABEL, STAGE_LABEL, StagePill, WITHDRAWN_LABEL, WORK_AUTH_STATUS } from './common';
import ScreenDialog, { csv, PayInput, screenLine } from './ScreenDialog';
import { AlexCancelDialog, AlexMoveDialog, AlexRescheduleDialog, AlexScheduleDialog } from './AlexDialogs';
import { RoundScheduleDialog } from './InterviewDialogs';

// One job on the candidate profile (CandProfile / RejectFlow, bundles 4a-2, 4c, 4d): a stage stepper (Sourced →
// Recruiter screen → AI screen → R1..Rn → Offer → BGV → Joined) and one action bar — the one next step for this state,
// Log call / E-mail / Note, Shortlist, Pending, Reject, and ⋯ (Withdraw, Move back, Reassign, Transfer). The server
// enforces the step-by-step rules and sends `progress`; the card only draws it. Reject / pending / withdraw take
// structured reasons (reported on later); the recruiter screen opens as its own pop-up (ScreenDialog). While someone
// else's lock holds, only a note or "Request transfer" is offered (the server enforces it too).

function useAct(onChanged) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (path, method, body, msg) => {
    setBusy(true); setError('');
    try { const r = await api(path, { method, body }); if (msg) toast.show(msg); onChanged && onChanged(r); setBusy(false); return r; }
    catch (e) { setError(e.message); setBusy(false); throw e; }
  };
  return { busy, error, setError, run };
}

function useReasons(appId, given) {
  const [opts, setOpts] = useState(given ? given.opts : null);
  const [ctx, setCtx] = useState(given ? given.ctx : null);
  useEffect(() => {
    if (given) return;                                  // 5b: the HM's candidate page sends them (no Recruit needed)
    api('/api/candidates/reasons').then(setOpts).catch(() => setOpts({ reject: [], pending: [], withdrawn: [], channels: [], not_looking: [], work_authorization: [] }));
    if (appId) api(`/api/applications/${appId}/reason-context`).then(setCtx).catch(() => setCtx({ skills: [], checks: [], strengths: {} }));
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { opts, ctx };
}

const AUTH_CHECK_LABEL = {
  phone: 'Phone number', phone_line: 'Line type (mobile / VoIP)', email: 'E-mail domain', email_age: 'E-mail age', apply_location: 'Application location',
  linkedin: 'LinkedIn', employers: 'Employers', resume: 'Duplicate resume', shared: 'Shared contacts', dates: 'Work history dates', titles: 'Titles vs. years',
  github: 'GitHub', voice_face: 'Voice & face', id_liveness: 'ID + liveness',
};
const NoteBox = ({ value, onChange, label = 'Note', required = false, rows = 2 }) => (
  <label className="block mt-3"><span className={labelCls}>{label}{required ? ' * (at least 10 characters)' : ' (optional)'}</span>
    <textarea className={input} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} maxLength={2000} data-testid="reason-note" />
    <PrivacyHint /></label>
);
const fmtBand = (b) => (b && (b.min != null || b.max != null) ? `${b.currency || ''} ${[b.min, b.max].filter((x) => x != null).map((x) => Number(x).toLocaleString()).join('–')} / ${b.period || 'year'}`.trim() : 'Not set on the job');

function RejectFields({ code, d, set, ctx, opts, app }) {
  const pick = (key, x) => { const cur = d[key] || []; set(key, cur.includes(x) ? cur.filter((y) => y !== x) : [...cur, x]); };
  if (code === 'compensation') {
    return (<div className="space-y-2 mt-3" data-testid="fields-compensation">
      <PayInput label="Expected pay *" value={d.expected_pay} onChange={(v) => set('expected_pay', v)} />
      <p className="text-xs text-[#64748B]">The job’s band: <b data-testid="job-band">{fmtBand(ctx && ctx.job_band)}</b></p></div>);
  }
  if (code === 'wont_relocate') {
    return (<div className="grid sm:grid-cols-2 gap-2 mt-3">
      <label className="block"><span className={labelCls}>Current location *</span><input className={input} value={d.current_location ?? ((app.screen || {}).current_location || '')} onChange={(e) => set('current_location', e.target.value)} /></label>
      <label className="block"><span className={labelCls}>Would move to (optional, comma-separated)</span><input className={input} value={(d.willing_locations || []).join(', ')} onChange={(e) => set('willing_locations', csv(e.target.value))} /></label></div>);
  }
  if (code === 'work_authorization') {
    return (<div className="grid sm:grid-cols-2 gap-2 mt-3">
      <label className="block"><span className={labelCls}>Status needed *</span>
        <select className={input} value={d.status || ''} onChange={(e) => set('status', e.target.value)} data-testid="wa-status"><option value="">Choose…</option>
          {WORK_AUTH_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
      <label className="block"><span className={labelCls}>Type{d.status === 'other' ? ' *' : ' (optional)'}</span><input className={input} value={d.type || ''} maxLength={60} onChange={(e) => set('type', e.target.value)} placeholder="e.g. H-1B" /></label></div>);
  }
  if (code === 'notice_period') {
    return (<label className="block mt-3"><span className={labelCls}>Notice (days) *</span>
      <input type="number" min={0} max={365} className={input} value={d.days ?? ((app.screen || {}).notice_days ?? '')} onChange={(e) => set('days', e.target.value === '' ? null : Number(e.target.value))} data-testid="notice-days" /></label>);
  }
  if (code === 'skills_mismatch' || code === 'failed_technical') {
    return (<fieldset className="mt-3"><legend className={labelCls}>Missing skills * (from the job’s criteria)</legend>
      <div className="flex flex-wrap gap-1.5" data-testid="missing-skills">{((ctx && ctx.skills) || []).map((x) => (
        <label key={x} className={`inline-flex items-center gap-1.5 text-xs border rounded-lg px-2 py-1 cursor-pointer ${(d.missing_skills || []).includes(x) ? 'border-[#0066CC] bg-[#F5F9FF]' : 'border-[#E6E9F0]'}`}>
          <input type="checkbox" checked={(d.missing_skills || []).includes(x)} onChange={() => pick('missing_skills', x)} />{x}</label>))}
        {ctx && !ctx.skills.length && <span className="text-xs text-[#64748B]">This job has no skill criteria — use Other.</span>}</div></fieldset>);
  }
  if (code === 'over_qualified' || code === 'under_qualified') {
    const r = (ctx && ctx.range) || {};
    return <p className="text-sm text-[#334155] mt-3" data-testid="years-auto">Candidate: <b>{ctx && ctx.years != null ? `~${ctx.years} yrs` : 'years not known'}</b> · Job: <b>{r.min ?? '?'}–{r.max ?? '?'} yrs</b> <span className="text-xs text-[#64748B]">(filled in from the resume)</span></p>;
  }
  if (code === 'not_looking') {
    return (<label className="block mt-3"><span className={labelCls}>Why *</span>
      <select className={input} value={d.sub_reason || ''} onChange={(e) => set('sub_reason', e.target.value)}><option value="">Choose…</option>
        {((opts && opts.not_looking) || []).map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}</select></label>);
  }
  if (code === 'failed_authenticity') {
    return (<fieldset className="mt-3"><legend className={labelCls}>Which checklist item(s) *</legend>
      <div className="flex flex-wrap gap-1.5">{((ctx && ctx.checks) || []).map((x) => (
        <label key={x} className="inline-flex items-center gap-1.5 text-xs border border-[#E6E9F0] rounded-lg px-2 py-1"><input type="checkbox" checked={(d.checks || []).includes(x)} onChange={() => pick('checks', x)} />{AUTH_CHECK_LABEL[x] || x}</label>))}</div></fieldset>);
  }
  return null;
}

// onSubmit (5b): the HM's Reject — the same reason / details / e-mail / talent pool, sent with their decision
export function RejectDialog({ app, candidate, onClose, onDone, onSubmit, given }) {
  const { opts, ctx } = useReasons(app.id, given);
  const [own, setOwn] = useState({ busy: false, error: '' });
  const [reason, setReason] = useState('');
  const [d, setD] = useState({});
  const [note, setNote] = useState('');
  const [mode, setMode] = useState('send_later');
  const [pool, setPool] = useState(true);
  const [preview, setPreview] = useState(null);
  const { busy, error, run } = useAct(onDone);
  const set = (k, v) => setD({ ...d, [k]: v });
  const details = { ...d, note };
  if (reason === 'wont_relocate' && d.current_location === undefined) details.current_location = (app.screen || {}).current_location || '';
  if (reason === 'notice_period' && d.days === undefined) details.days = (app.screen || {}).notice_days ?? null;
  const submit = () => (onSubmit
    ? (setOwn({ busy: true, error: '' }), onSubmit({ reason, details, email_mode: mode, talent_pool: pool }).then(() => setOwn({ busy: false, error: '' }), (e) => setOwn({ busy: false, error: e.message })))
    : run(`/api/applications/${app.id}/stage`, 'PATCH', { stage: 'rejected', reason, details, email_mode: mode, talent_pool: pool }, 'Candidate rejected').catch(() => {}));
  const showPreview = () => api('/api/candidates/settings/rejection-preview', { method: 'POST', body: {} }).then(setPreview).catch(() => setPreview({ subject: 'Preview is available to company admins', body: '' }));
  const opt = (k, title, sub) => (
    <label className={`flex gap-3 items-start border rounded-xl px-3 py-2.5 cursor-pointer ${mode === k ? 'border-[#0066CC] bg-[#F5F9FF]' : 'border-[#E6E9F0]'}`}>
      <input type="radio" name="tell" checked={mode === k} onChange={() => setMode(k)} className="mt-1" />
      <span><span className="block text-sm font-semibold">{title}</span><span className="block text-xs text-[#64748B]">{sub}</span></span>
    </label>
  );
  return (
    <Modal open title="Reject candidate" onClose={onClose} width="max-w-lg"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnDanger} disabled={busy || own.busy || !reason || (reason === 'other' && note.trim().length < 10)} onClick={submit} data-testid="reject-confirm">Reject candidate</button></>}>
      <p className="text-sm text-[#64748B] mb-3">{candidate.full_name} · {app.job_title}{app.req_id ? ` (${app.req_id})` : ''} · stage: {STAGE_LABEL[app.stage]}</p>
      <fieldset><legend className={labelCls}>Reason (required — used in reporting)</legend>
        <div className="grid sm:grid-cols-2 gap-1.5" data-testid="reject-reasons">
          {((opts && opts.reject) || []).map((x) => (
            <label key={x.code} className={`flex items-center gap-2 text-sm border rounded-lg px-2.5 py-2 cursor-pointer ${reason === x.code ? 'border-[#0066CC] bg-[#F5F9FF]' : 'border-[#E6E9F0]'}`}>
              <input type="radio" name="reason" checked={reason === x.code} onChange={() => { setReason(x.code); setD({}); }} />{x.label}</label>
          ))}
        </div></fieldset>
      <RejectFields code={reason} d={d} set={set} ctx={ctx} opts={opts} app={app} />
      <NoteBox value={note} onChange={setNote} label="Details (visible to your team only)" required={reason === 'other'} />
      <fieldset className="mt-3"><legend className={labelCls}>Tell the candidate?</legend>
        <div className="space-y-2">
          {opt('send_now', 'Send now', 'A polite “not moving forward” e-mail')}
          {opt('send_later', 'Send later', 'In 2 business days (feels less automated)')}
          {opt('dont_send', 'Don’t send', 'I’ll tell them myself')}
        </div></fieldset>
      {mode !== 'dont_send' && <button type="button" className="text-sm text-[#0066CC] font-semibold mt-2 hover:underline" onClick={showPreview}>Preview template</button>}
      {preview && <div className="mt-2 border border-[#E6E9F0] rounded-lg p-3 bg-[#F8FAFC] text-sm"><b>{preview.subject}</b><p className="whitespace-pre-line mt-1 text-[#334155]">{preview.body}</p></div>}
      <label className="inline-flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={pool} onChange={(e) => setPool(e.target.checked)} /> Keep in talent pool for future roles</label>
      <div className="mt-3"><InlineError>{error || own.error}</InlineError></div>
    </Modal>
  );
}

export function PendingDialog({ app, onClose, onDone }) {
  const { opts } = useReasons(null);
  const [reason, setReason] = useState('');
  const [when, setWhen] = useState('');
  const [channel, setChannel] = useState('');
  const [note, setNote] = useState('');
  const { busy, error, run } = useAct(onDone);
  const row = ((opts && opts.pending) || []).find((x) => x.code === reason);
  const needsDate = !!(row && row.follow_up_required);
  const submit = () => run(`/api/applications/${app.id}/pending`, 'POST',
    { reason, details: { follow_up_at: when ? new Date(when).toISOString() : null, channel: channel || null, note } }, 'Marked pending').catch(() => {});
  return (
    <Modal open title="Pending" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !reason || (needsDate && !when)} onClick={submit} data-testid="pending-confirm">Save</button></>}>
      <div className="space-y-1.5">{((opts && opts.pending) || []).map((x) => (
        <label key={x.code} className="flex items-center gap-2 text-sm"><input type="radio" name="pending" checked={reason === x.code} onChange={() => setReason(x.code)} />{x.label}</label>))}</div>
      {reason && <label className="block mt-3"><span className={labelCls}>Follow up on{needsDate ? ' * — you’ll get a reminder then' : ' (optional)'}</span>
        <input type="datetime-local" className={input} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="callback-at" /></label>}
      {reason && <label className="block mt-3"><span className={labelCls}>Channel</span>
        <select className={input} value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="pending-channel"><option value="">—</option>
          {((opts && opts.channels) || []).map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}</select></label>}
      <NoteBox value={note} onChange={setNote} />
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function WithdrawDialog({ app, onClose, onDone }) {
  const { opts } = useReasons(null);
  const [reason, setReason] = useState('');
  const [company, setCompany] = useState('');
  const [offered, setOffered] = useState(null);
  const [note, setNote] = useState('');
  const { busy, error, run } = useAct(onDone);
  const row = ((opts && opts.withdrawn) || []).find((x) => x.code === reason);
  const details = { note, ...(row && row.offer ? { competing_company: company || null, offered_pay: offered } : {}) };
  return (
    <Modal open title="Candidate withdrew" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !reason || (reason === 'other' && note.trim().length < 10)} data-testid="withdraw-confirm"
          onClick={() => run(`/api/applications/${app.id}/stage`, 'PATCH', { stage: 'withdrawn', reason, details }, 'Marked withdrawn').catch(() => {})}>Save</button></>}>
      <div className="space-y-1.5">{((opts && opts.withdrawn) || []).map((x) => (
        <label key={x.code} className="flex items-center gap-2 text-sm"><input type="radio" name="wd" checked={reason === x.code} onChange={() => setReason(x.code)} />{x.label}</label>))}</div>
      {row && row.offer && (
        <div className="space-y-2 mt-3" data-testid="offer-fields">
          <label className="block"><span className={labelCls}>Competing company (optional)</span><input className={input} value={company} maxLength={120} onChange={(e) => setCompany(e.target.value)} /></label>
          <PayInput label="Offered pay (optional)" value={offered} onChange={setOffered} />
        </div>
      )}
      <NoteBox value={note} onChange={setNote} required={reason === 'other'} />
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function SubmitDialog({ app, onClose, onDone, onMissing }) {
  const { ctx } = useReasons(app.id);
  const [tags, setTags] = useState([]);
  const { busy, error, run } = useAct(onDone);
  const all = Object.entries((ctx && ctx.strengths) || {});
  const flip = (k) => setTags(tags.includes(k) ? tags.filter((x) => x !== k) : tags.length < 3 ? [...tags, k] : tags);
  const go = () => run(`/api/applications/${app.id}/stage`, 'PATCH', { stage: 'submitted', strengths: tags.length ? tags : null }, 'Submitted')
    .catch((e) => { if (e.detail && e.detail.missing) onMissing(e.detail.message); });
  return (
    <Modal open title="Submit to the hiring manager" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={busy} onClick={go} data-testid="submit-confirm">Submit</button></>}>
      <p className={labelCls}>Strengths (optional, up to 3 — from the job’s criteria)</p>
      <div className="flex flex-wrap gap-1.5" data-testid="strengths">{all.map(([k, l]) => (
        <button key={k} type="button" aria-pressed={tags.includes(k)} onClick={() => flip(k)}
          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${tags.includes(k) ? 'border-[#0066CC] bg-[#0066CC] text-white' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>{l}</button>))}</div>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export function LogDialog({ kind, app, candidate, onClose, onDone }) {
  const [body, setBody] = useState('');
  const [mins, setMins] = useState('');
  const { busy, error, run } = useAct(onDone);
  const title = { call: 'Log a call', email: 'Log an e-mail', note: 'Add a note' }[kind];
  return (
    <Modal open title={title} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || (kind === 'note' && !body.trim())} data-testid="log-confirm"
          onClick={() => run(`/api/candidates/${candidate.id}/activities`, 'POST', { kind, body, application_id: app ? app.id : null,
            duration_s: kind === 'call' && mins ? Math.round(Number(mins) * 60) : null }, 'Saved').catch(() => {})}>Save</button></>}>
      {app && kind !== 'note' && !app.owner_user_id && <p className="text-xs text-[#64748B] mb-2">Logging this makes you the owner of {candidate.full_name} for {app.job_title}.</p>}
      {kind === 'call' && <label className="block mb-3"><span className={labelCls}>Length (minutes)</span><input type="number" min={0} className={input} value={mins} onChange={(e) => setMins(e.target.value)} /></label>}
      <label className="block"><span className={labelCls}>{kind === 'note' ? 'Note' : 'Notes (optional)'}</span><textarea className={input} rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} /><PrivacyHint /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function TransferDialog({ app, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const { busy, error, run } = useAct(onDone);
  return (
    <Modal open title="Request transfer" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !reason.trim()} onClick={() => run(`/api/applications/${app.id}/transfer-requests`, 'POST', { reason }, 'Request sent').catch(() => {})}>Send request</button></>}>
      <p className="text-sm text-[#64748B] mb-2">{app.owner} owns this candidate for {app.job_title}. They, their manager or a company admin can approve.</p>
      <label className="block"><span className={labelCls}>Why do you need it?</span><textarea className={input} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} /><PrivacyHint /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function ReassignDialog({ app, onClose, onDone }) {
  const [people, setPeople] = useState([]);
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const { busy, error, run } = useAct(onDone);
  useEffect(() => { api('/api/candidates/recruiters').then((d) => setPeople(d.recruiters)).catch(() => setPeople([])); }, []);
  return (
    <Modal open title="Reassign owner" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !to || !reason.trim()} onClick={() => run(`/api/applications/${app.id}/owner`, 'POST', { user_id: Number(to), reason }, 'Reassigned').catch(() => {})}>Reassign</button></>}>
      <label className="block"><span className={labelCls}>New owner</span>
        <select className={input} value={to} onChange={(e) => setTo(e.target.value)}><option value="">Choose…</option>
          {people.filter((p) => p.id !== app.owner_user_id).map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}</select></label>
      <label className="block mt-3"><span className={labelCls}>Reason (recorded)</span><input className={input} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} /><PrivacyHint /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

function MoveBackDialog({ app, onClose, onDone }) {
  const opts = (app.progress && app.progress.back_to) || [];
  const [to, setTo] = useState(opts.length ? String(opts.length - 1) : '');
  const [reason, setReason] = useState('');
  const { busy, error, run } = useAct(onDone);
  const t = opts[Number(to)];
  return (
    <Modal open title="Move back" onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !t || reason.trim().length < 3} data-testid="move-back-confirm"
          onClick={() => run(`/api/applications/${app.id}/stage`, 'PATCH', { stage: t.stage, interview_round: t.round, back_reason: reason.trim() }, `Moved back to ${t.label}`).catch(() => {})}>Move back</button></>}>
      <label className="block"><span className={labelCls}>Back to</span>
        <select className={input} value={to} onChange={(e) => setTo(e.target.value)} data-testid="move-back-to">
          {opts.map((o, i) => <option key={`${o.stage}${o.round || ''}`} value={i}>{o.label}</option>)}</select></label>
      <label className="block mt-3"><span className={labelCls}>Reason * (recorded in the audit log)</span>
        <textarea className={input} rows={3} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} data-testid="move-back-reason" /><PrivacyHint /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

const STEP_TONE = {
  done: 'bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]', current: 'bg-[#0066CC] text-white border-[#0066CC]',
  out: 'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]', locked: 'bg-white text-[#94A3B8] border-[#E6E9F0]',
};
const STEP_ICON = { done: '✓', current: '●', out: '✗', locked: '🔒' };
const STEP_SAYS = { done: 'done', current: 'current', out: 'out', locked: 'locked' };

export function Stepper({ steps, className = 'mt-3' }) {
  return (
    <ol className={`flex flex-wrap items-start gap-y-2 ${className}`} data-testid="stepper" aria-label="Stages">
      {steps.map((st, i) => (
        <li key={st.key} className="flex items-center" data-testid={`step-${st.key}`} data-state={st.state}
          title={st.panel && st.panel.length ? `Interviewers: ${st.panel.join(', ')}` : undefined}>
          {i > 0 && <span className={`w-3 sm:w-5 h-px mx-0.5 ${st.state === 'locked' ? 'bg-[#E6E9F0]' : 'bg-[#86EFAC]'}`} aria-hidden="true" />}
          <span className="flex flex-col items-center">
            <span className={`inline-flex items-center gap-1 px-2 py-[3px] rounded-full border text-[11px] font-semibold whitespace-nowrap ${STEP_TONE[st.state]}`}>
              <span aria-hidden="true" className={st.state === 'locked' ? 'text-[9px]' : ''}>{STEP_ICON[st.state]}</span>{st.label}
              <span className="sr-only"> — {STEP_SAYS[st.state]}</span></span>
            {st.sub && <span className="text-[10.5px] text-[#64748B] mt-0.5 whitespace-nowrap" data-testid="step-sub">{st.sub}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

export const iconBtn = 'inline-flex shrink-0 items-center justify-center w-9 h-9 rounded-lg border border-[#E6E9F0] bg-white text-[#334155] hover:bg-[#F5F7FB] disabled:opacity-50';
const rejectBtn = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-red-600 bg-white text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50';
const wide = ' hidden lg:inline-flex shrink-0';          // lower-priority buttons: in "⋯" below lg, so the bar never wraps

export function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [open]);
  if (!items.length) return null;
  return (
    <span className="relative shrink-0" ref={ref}>
      <button type="button" className={iconBtn} aria-label="More actions" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="more-menu">⋯</button>
      {open && (
        <span role="menu" className="absolute right-0 top-10 z-30 min-w-[220px] bg-white border border-[#E6E9F0] rounded-lg shadow-lg py-1 flex flex-col">
          {items.map((it) => (
            <button key={it.key} type="button" role="menuitem" data-testid={`menu-${it.key}`}
              className={`text-left px-3 py-2 text-sm hover:bg-[#F5F7FB] ${it.danger ? 'text-red-700' : ''} ${it.cls || ''}`}
              onClick={() => { setOpen(false); it.onClick(); }}>{it.label}</button>
          ))}
        </span>
      )}
    </span>
  );
}

export const ownerText = (app) => (app.owner ? `Owned by ${app.owner} · since ${fmtDateTime(app.owned_since)}`
  : app.last_progressed ? `No current owner · last progressed by ${app.last_progressed.by || 'a former user'} on ${fmtDate(app.last_progressed.at)}`
    : 'Not owned yet — the first logged call or e-mail claims it');

// Pending / rejected / withdrawn: why, and the captured details
export function AppStatus({ app }) {
  return <>
    {app.status === 'pending' && <p className="text-xs text-[#92400E] mt-1">Pending: {PENDING_LABEL[app.pending_reason] || 'waiting'}{app.callback_at ? ` · call back ${fmtDateTime(app.callback_at)}` : ''}</p>}
    {app.status === 'rejected' && <p className="text-xs text-[#991B1B] mt-1">Rejected{app.rejected_by === 'hm' ? ' by the hiring manager' : ''}: {REJECT_LABEL[app.reject_reason] || app.reject_reason}
      {app.reject_email_sent_at ? ' · e-mail sent' : app.reject_email_due_at ? ` · e-mail goes ${fmtDateTime(app.reject_email_due_at)}` : ''}</p>}
    {app.status === 'withdrawn' && <p className="text-xs text-[#991B1B] mt-1">Withdrew: {WITHDRAWN_LABEL[app.withdrawn_reason] || app.withdrawn_reason}</p>}
    {app.out_details && <p className="text-xs text-[#7F1D1D] mt-0.5" data-testid="out-details">{app.out_details}</p>}
  </>;
}

// Someone else's lock, and a pending transfer request (approve / decline / cancel)
export function TransferBanner({ app, onChanged }) {
  const { user } = storedSession();
  const { busy, error, run } = useAct(onChanged);
  const tr = app.transfer_request;
  return <>
    {!app.can_act && app.can_work !== false && <p className="text-sm bg-[#F8FAFC] border border-[#E6E9F0] rounded-lg px-3 py-2 mt-3">{app.owner} owns this candidate for this job — you can view and add notes.</p>}
    {tr && (
      <div className="text-sm bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2 mt-3 flex flex-wrap items-center gap-2" data-testid="transfer-request">
        <span className="flex-1"><b>{tr.requested_by_name}</b> asked to take over: “{tr.reason}”</span>
        {tr.requested_by !== user.id ? <>
          <button className={btnSecondary} disabled={busy} onClick={() => run(`/api/transfer-requests/${tr.id}/decide`, 'POST', { approve: false }, 'Declined').catch(() => {})}>Decline</button>
          <button className={btnPrimary} disabled={busy} onClick={() => run(`/api/transfer-requests/${tr.id}/decide`, 'POST', { approve: true }, 'Transferred').catch(() => {})}>Approve</button></>
          : <button className={btnSecondary} disabled={busy} onClick={() => run(`/api/transfer-requests/${tr.id}/cancel`, 'POST', undefined, 'Request cancelled').catch(() => {})}>Cancel request</button>}
        <InlineError>{error}</InlineError>
      </div>
    )}
  </>;
}

// One line: the next step, call / e-mail / note, Shortlist, Pending ▾, Reject ▾, "⋯" (+ `extra` items, e.g. the
// profile's). The dialogs are the parent's state (dialog / setDialog), so other parts of the page can open the screen.
export function ActionBar({ app, candidate, onChanged, dialog, setDialog, extra = [] }) {
  const { user } = storedSession();
  const admin = isAdmin(user);
  const { busy, error, setError, run } = useAct(onChanged);
  const done = () => { setDialog(null); onChanged && onChanged(); };
  const closed = ['rejected', 'withdrawn', 'joined'].includes(app.status);
  const viewOnly = app.can_work === false;                // not on this job (and not its owner): read-only, notes only
  const lockedOut = !app.can_act || viewOnly;
  const pg = app.progress || { steps: [], next: null, shortlist: {}, back_to: [] };
  const nx = pg.next;
  const sl = pg.shortlist || {};
  const missingScreen = (msg) => { setDialog('screen'); setError(msg); };
  const go = () => {
    const base = `/api/applications/${app.id}`;
    if (nx.kind === 'advance') {
      if (nx.stage === 'submitted') { setDialog('submit'); return; }
      run(`${base}/stage`, 'PATCH', { stage: nx.stage, interview_round: nx.round }, `Moved to ${nx.round ? `interview ${nx.round}` : STAGE_LABEL[nx.stage]}`)
        .then(() => { if (nx.stage === 'recruiter_screen') setDialog('screen'); }).catch(() => {});
    } else if (nx.kind === 'screen') setDialog('screen');
    else if (nx.kind === 'shortlist') run(`${base}/shortlist`, 'POST', { on: true }, 'Shortlisted').catch(() => {});
    else if (nx.kind === 'schedule_alex') setDialog('alex');
    else if (nx.kind === 'schedule_round') setDialog('round');           // 5a: date, time, panel, link — invites sent             // 4f: one step — time + time zone, the server does the rest
    else if (nx.kind === 'remind_hm') run(`${base}/remind-hm`, 'POST', undefined, 'Reminder sent to the hiring manager').catch(() => {});
    else if (nx.kind === 'state') run(`${base}/stage-state`, 'POST', { state: nx.state }, 'Updated').catch(() => {});
    else if (nx.kind === 'reopen') run(`${base}/reopen`, 'POST', undefined, 'Reopened').catch(() => {});
  };
  const canPrimary = nx && !lockedOut && (nx.kind === 'reopen' ? app.status !== 'joined' : !closed);
  const open = !lockedOut && !closed;
  const pending = app.status === 'pending';
  const unpend = () => run(`/api/applications/${app.id}/resume`, 'POST', undefined, 'No longer pending').catch(() => {});
  const more = [
    open && { key: 'pending', label: pending ? 'Not pending' : 'Pending…', cls: 'lg:hidden', onClick: () => (pending ? unpend() : setDialog('pending')) },
    open && { key: 'reject', label: 'Reject…', cls: 'lg:hidden', danger: true, onClick: () => setDialog('reject') },
    open && { key: 'withdraw', label: 'Withdrew…', onClick: () => setDialog('withdraw') },
    open && (pg.back_to || []).length > 0 && { key: 'move-back', label: 'Move back…', onClick: () => setDialog('move_back') },
    admin && app.owner_user_id && { key: 'reassign', label: 'Reassign owner…', onClick: () => setDialog('reassign') },
    !viewOnly && lockedOut && !app.transfer_request && { key: 'transfer', label: 'Request transfer…', onClick: () => setDialog('transfer') },
    ...extra,
  ].filter(Boolean);
  const shortlistInfo = app.shortlisted_info;
  return <>
    {viewOnly && <p className="text-sm bg-[#F8FAFC] border border-[#E6E9F0] rounded-lg px-3 py-2 mb-2" data-testid="view-only">{app.view_reason === 'hm'
      ? 'View only — as the hiring manager you review and decide on the Alex screen; the recruiters run the other steps. You can add notes.'
      : 'View only — you’re not on this job. You can view and add notes.'}</p>}
    <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap min-w-0" data-testid="action-bar">
      {canPrimary && (
        <button className={btnPrimary + ' shrink-0'} disabled={busy || !!nx.disabled} title={nx.disabled ? 'Nothing to do here until the Alex screen is done' : pg.needs || undefined}
          onClick={go} data-testid="next-step">{nx.label}</button>
      )}
      {open && <>
        <button className={iconBtn} aria-label="Log call" title="Log call" onClick={() => setDialog('call')}>📞</button>
        <button className={iconBtn} aria-label="E-mail" title="E-mail" onClick={() => setDialog('email')}>✉</button>
      </>}
      <button className={iconBtn} aria-label="Note" title="Note" onClick={() => setDialog('note')}>📝</button>
      {open && <>
        {app.shortlisted && shortlistInfo
          ? <button className={btnSecondary + wide} disabled title={`Shortlisted ${fmtDate(shortlistInfo.at)}${shortlistInfo.by ? ` by ${shortlistInfo.by}` : ''}`} data-testid="shortlisted">
            Shortlist ✓ <span className="font-normal text-xs text-[#64748B]">{fmtDate(shortlistInfo.at)}{shortlistInfo.by ? ` · ${shortlistInfo.by}` : ''}</span></button>
          : sl.hm_decides ? <button className={btnSecondary + wide} disabled title="HM decides after the Alex screen" data-testid="hm-decides">Shortlist</button>
            : sl.needs_screen ? <button className={btnSecondary + wide} disabled title="Save the recruiter screen first" data-testid="shortlist-locked">Shortlist</button>
              : null}
        {pending
          ? <button className={btnSecondary + wide} disabled={busy} onClick={unpend}>Not pending</button>
          : <button className={btnSecondary + wide} onClick={() => setDialog('pending')}>Pending ▾</button>}
        <button className={rejectBtn + wide} onClick={() => setDialog('reject')} data-testid="reject">Reject ▾</button>
      </>}
      <MoreMenu items={more} />
    </div>
    <InlineError>{error}</InlineError>
    {dialog === 'screen' && <ScreenDialog app={app} candidate={candidate} canAct={open} onClose={() => setDialog(null)} onSaved={done} />}
    {['call', 'email', 'note'].includes(dialog) && <LogDialog kind={dialog} app={app} candidate={candidate} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'reject' && <RejectDialog app={app} candidate={candidate} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'pending' && <PendingDialog app={app} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'withdraw' && <WithdrawDialog app={app} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'transfer' && <TransferDialog app={app} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'reassign' && <ReassignDialog app={app} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'move_back' && <MoveBackDialog app={app} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'submit' && <SubmitDialog app={app} onClose={() => setDialog(null)} onDone={done} onMissing={missingScreen} />}
    {dialog === 'alex' && <AlexScheduleDialog appId={app.id} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'round' && <RoundScheduleDialog appId={app.id} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'alex_reschedule' && <AlexRescheduleDialog app={app} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'alex_cancel' && <AlexCancelDialog app={app} onClose={() => setDialog(null)} onDone={done} />}
    {dialog === 'alex_move' && app.alex && <AlexMoveDialog interviewId={app.alex.interview_id} onClose={() => setDialog(null)} onDone={done} />}
  </>;
}

export function ScreenLine({ app, onOpen, className = '' }) {
  const sm = app.screen_summary;
  if (!sm || !sm.saved_at) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 text-sm bg-[#F8FAFC] border border-[#E6E9F0] rounded-lg px-3 py-2 ${className}`} data-testid="screen-line">
      <span className="flex-1">{screenLine(sm)}</span>
      {onOpen && <button className="text-[#0066CC] font-semibold hover:underline" onClick={onOpen} data-testid="open-screen">Open screen</button>}
    </div>
  );
}

export default function ApplicationCard({ app, candidate, onChanged }) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  return (
    <article id={`app-${app.id}`} className={card + ' p-4 scroll-mt-20'} data-testid={`application-${app.id}`}>
      <div className="min-w-0">
        <button className="font-bold text-[#0F172A] hover:text-[#0066CC] text-left" onClick={() => navigate(`/jobs/${app.requirement_id}`)}>{app.job_title}</button>
        <span className="text-xs text-[#64748B] ml-2">{app.req_id}</span>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <StagePill stage={app.stage} status={app.status} round={app.interview_round} />
          <span className="text-xs text-[#64748B]" data-testid="owner-text">{ownerText(app)}</span>
        </div>
        <AppStatus app={app} />
      </div>
      <Stepper steps={(app.progress && app.progress.steps) || []} />
      <TransferBanner app={app} onChanged={onChanged} />
      <ScreenLine app={app} onOpen={app.can_work === false ? null : () => setDialog('screen')} className="mt-3" />
      <div className="mt-3"><ActionBar app={app} candidate={candidate} onChanged={onChanged} dialog={dialog} setDialog={setDialog} /></div>
    </article>
  );
}
